import { toast } from 'react-toastify';
import type { GroupSelection } from './groupFilter';
import type { MakerFlag } from '../../types/entry.types';
import type { HedgeMode } from '../shared/counterHedgeMode';

/**
 * Group-aware payload builders for the two "apply to all" bulk actions on
 * the opener tab. Both keep the no-filter ("All groups") behavior byte-
 * identical to the pre-group payloads — that path is regression-critical.
 */

/** Backend caps `pairIds` at 100 per /pairs/bulk-opener-enabled request. */
export const BULK_ENABLE_PAIR_IDS_CHUNK = 100;
/** Backend caps `ruleIds` at 500 per /rules/bulk-params request. */
export const BULK_PARAMS_RULE_IDS_CHUNK = 500;

export function chunkIds(ids: string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

export interface BulkEnablePayload {
  scope: 'all' | 'ids';
  enabled: boolean;
  pairIds?: string[];
  groupId?: string;
}

/**
 * "All ON / All OFF" payload(s). A selected group narrows the server-side
 * scope:'all' via `groupId`; "Ungrouped" has NO server-side narrowing, so
 * it reuses the scope:'ids' path over the currently-visible (ungrouped)
 * pair ids, chunked at the backend cap.
 */
export function buildBulkEnableAllPayloads(
  selection: GroupSelection,
  enabled: boolean,
  visiblePairIds: string[]
): BulkEnablePayload[] {
  if (selection.kind === 'all') return [{ scope: 'all', enabled }];
  if (selection.kind === 'group') {
    return [{ scope: 'all', enabled, groupId: selection.groupId }];
  }
  return chunkIds(visiblePairIds, BULK_ENABLE_PAIR_IDS_CHUNK).map(pairIds => ({
    scope: 'ids',
    pairIds,
    enabled
  }));
}

/**
 * Honest pair total for the "All ON / All OFF" confirm dialog. "All groups"
 * and a real group flip server-side (`scope:'all'` [+ `groupId`]) — the
 * WHOLE group-scoped universe, not just the search-narrowed visible rows —
 * so the dialog must quote the UNSEARCHED group-scoped pair count.
 * "Ungrouped" rides the scope:'ids' path over the visible rows, so there
 * the visible selectable count is the accurate one.
 */
export function bulkAllConfirmCount(
  selection: GroupSelection,
  groupPairCount: number,
  visibleSelectableCount: number
): number {
  return selection.kind === 'ungrouped'
    ? visibleSelectableCount
    : groupPairCount;
}

/**
 * Pair ids the "All ON / All OFF" write actually flips — the id-set twin of
 * `bulkAllConfirmCount`. "All groups" and a real group flip server-side
 * (`scope:'all'` [+ `groupId`]), so the post-write settle + unconfirmed
 * re-check must cover the UNSEARCHED group-scoped pair ids, not just the
 * search-narrowed visible rows. "Ungrouped" rides the scope:'ids' path over
 * the visible rows, so there the visible selectable ids are the target.
 */
export function bulkAllTargetIds(
  selection: GroupSelection,
  groupPairIds: string[],
  visibleSelectableIds: string[]
): string[] {
  return selection.kind === 'ungrouped' ? visibleSelectableIds : groupPairIds;
}

export interface BulkParamsValues {
  thresholdSum?: number;
  size?: number;
  maxVolumeContracts?: number;
  /**
   * Maker size mode — dynamic sizes the maker to the counter top-of-book,
   * fixed rests the rule's full Size. Callers set the key ONLY when the
   * operator picked a mode (the bar's "—" leaves it out entirely), so the
   * no-mode payload stays byte-identical to the pre-mode one.
   */
  makerSizeMode?: 'dynamic' | 'fixed';
  /**
   * Counter-stuck market-hedge timeout (seconds). Callers set the key ONLY
   * when the operator filled the field (blank leaves it out entirely), same
   * contract as `makerSizeMode` — the no-value payload stays byte-identical.
   */
  counterStuckMarketHedgeSeconds?: number;
  /**
   * Keep posting the Polymarket maker even when the book sits far from the
   * threshold (illiquid markets). Callers set the key ONLY when the operator
   * toggled it (the bar's indeterminate state leaves it out entirely), same
   * contract as `makerSizeMode` — the no-value payload stays byte-identical.
   */
  disablePlacementGatePolymarket?: boolean;
  /** Same as `disablePlacementGatePolymarket` but for the Kalshi maker leg. */
  disablePlacementGateKalshi?: boolean;
  /**
   * Send the Polymarket maker with `postOnly` (the venue rejects it instead of
   * executing when it would be marketable, and the engine reprices). Callers
   * set the key ONLY when the operator toggled it (the bar's indeterminate
   * state leaves it out entirely), same contract as `disablePlacementGate*` —
   * the no-value payload stays byte-identical.
   */
  polyMakerPostOnly?: boolean;
  lockstepMaxAheadBatches?: number | null;
  /**
   * Counter-hedge pricing mode override. `null` clears the per-rule override
   * back to the global default — so this key, when the caller sets it, is
   * ALWAYS emitted (an explicit `null` for inherit), never omitted. The
   * dedicated hedge-mode bulk apply (`buildBulkHedgeModeDtos`) always sets it.
   */
  counterHedgeMode?: HedgeMode | null;
  /**
   * Counter/hedge price-ladder rung interval (ms). `null` clears the per-rule
   * override, handing the decision back to the group/subgroup fallback — so
   * this key, like `counterHedgeMode`, is ALWAYS emitted once the caller sets
   * it. The dedicated ladder bulk apply (`buildBulkLadderDtos`) always sets it.
   */
  counterHedgeLadderStepMs?: number | null;
  /**
   * Ladder adverse-move tick threshold. Same always-emitted contract as
   * `counterHedgeLadderStepMs` — the two travel together in one request.
   */
  counterHedgeLadderAdverseTicks?: number | null;
  /**
   * Group-wide forfait-protection arm/disarm — the bulk twin of the per-row
   * Forf checkbox. Callers set the key ONLY when the operator moved the
   * tristate off "no change", same contract as `disablePlacementGate*`.
   */
  forfaitProtectionEnabled?: boolean;
  /**
   * Group-wide Polymarket buy ceiling while the forfait regime is armed. Set
   * ONLY when the operator filled the field; a blank leaves it out entirely
   * (there is no "clear to inherit" here — the engine's own default rules).
   */
  forfaitPolyMaxBuyPrice?: number;
}

export interface BulkParamsDto extends BulkParamsValues {
  scope: 'all' | 'ids';
  ruleIds?: string[];
  /**
   * Server-side group narrowing for `scope:'all'` — stamps only that opener
   * group's rules (paused included), groupId XOR subgroupId, never both.
   */
  groupId?: string;
  subgroupId?: string;
  path: 'a' | 'b';
}

/**
 * Scope a bulk-params apply can target: the page's group filter (all / a real
 * group / ungrouped) OR a concrete subgroup nested inside the selected group.
 * Mirrors `BulkMakerSelection` — the subgroup branch is server-narrowed via
 * `subgroupId` instead of shipping the group-scoped rule ids (groupId XOR
 * subgroupId, never both).
 */
export type BulkParamSelection =
  | GroupSelection
  | { kind: 'subgroup'; subgroupId: string };

/**
 * Set-all threshold/size/maxV DTO(s) — a RULES op. "All groups" keeps the
 * single scope:'all' request; a group / Ungrouped filter resolves to the
 * PRE-SEARCH group-scoped pairs' rule ids (the whole group, not just the
 * search-narrowed visible rows — consistent with the group "All ON/OFF")
 * and sends scope:'ids', chunked defensively at the backend's 500-id cap;
 * a concrete subgroup narrows scope:'all' server-side via `subgroupId` (so
 * the backend stamps the whole subgroup without the UI shipping ids), exactly
 * like the subgroup maker-flag flip. An empty id list yields NO requests
 * (nothing to update — the caller still stages the pre-rule seed).
 */
export function buildBulkParamsDtos(
  selection: BulkParamSelection,
  path: 'a' | 'b',
  values: BulkParamsValues,
  scopedRuleIds: string[]
): BulkParamsDto[] {
  if (selection.kind === 'all') return [{ scope: 'all', path, ...values }];
  if (selection.kind === 'subgroup') {
    return [{ scope: 'all', subgroupId: selection.subgroupId, path, ...values }];
  }
  return chunkIds(scopedRuleIds, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    path,
    ...values
  }));
}

/**
 * Applies each bulk-params DTO sequentially and sums the per-chunk
 * `modified` counters into the single number the bar reports ("Updated N
 * rules"). Sequential on purpose — mirrors the other chunked bulk loops so
 * a mid-loop failure leaves a clean prefix applied.
 */
export async function applyBulkParamsDtos(
  dtos: BulkParamsDto[],
  apply: (dto: BulkParamsDto) => Promise<{ modified: number }>
): Promise<number> {
  let totalModified = 0;
  for (const dto of dtos) {
    totalModified += (await apply(dto)).modified;
  }
  return totalModified;
}

/**
 * Bulk counter-hedge-mode DTO(s) — stamps `counterHedgeMode` across the
 * selected scope via the same `/rules/bulk-params` endpoint. Distinct from
 * `buildBulkParamsDtos` (the Thr/Size/MaxV apply) in HOW it narrows a group:
 * it uses the server-side `groupId` scope (like the maker-flag/armed flips)
 * instead of resolving the group to `scope:'ids'` rule ids. "All groups" keeps
 * the single `scope:'all'` request; a real group narrows `scope:'all'`
 * server-side via `groupId`; a concrete subgroup narrows via `subgroupId`
 * (groupId XOR subgroupId, never both); "Ungrouped" rides `scope:'ids'` over
 * the group-scoped (pre-search) rule ids, chunked at the backend's 500-id cap.
 * `counterHedgeMode: null` clears the per-rule override back to the global
 * default and is ALWAYS emitted. An empty id list (ungrouped view with no
 * rules) yields NO requests.
 */
export function buildBulkHedgeModeDtos(
  selection: BulkParamSelection,
  path: 'a' | 'b',
  counterHedgeMode: HedgeMode | null,
  scopedRuleIds: string[]
): BulkParamsDto[] {
  if (selection.kind === 'all')
    return [{ scope: 'all', path, counterHedgeMode }];
  if (selection.kind === 'group')
    return [
      { scope: 'all', groupId: selection.groupId, path, counterHedgeMode }
    ];
  if (selection.kind === 'subgroup')
    return [
      { scope: 'all', subgroupId: selection.subgroupId, path, counterHedgeMode }
    ];
  return chunkIds(scopedRuleIds, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    path,
    counterHedgeMode
  }));
}

/**
 * Bulk counter/hedge-LADDER DTO(s) — stamps `counterHedgeLadderStepMs` +
 * `counterHedgeLadderAdverseTicks` across the selected scope via the same
 * `/rules/bulk-params` endpoint. Narrows a scope exactly like
 * `buildBulkHedgeModeDtos` for every scope it accepts (see the `all` carve-out
 * below): a real group via the server-side `groupId`, a
 * concrete subgroup via `subgroupId` (groupId XOR subgroupId, never both),
 * "Ungrouped" via `scope:'ids'` over the group-scoped (pre-search) rule ids,
 * chunked at the backend's 500-id cap. An empty id list yields NO requests.
 *
 * BOTH keys always ride together, `null` included: this apply expresses "the
 * ladder config for this scope is exactly these two boxes", which is what makes
 * an empty step the rollback lever (clear the per-rule override back to the
 * group fallback) instead of a silent no-op.
 *
 * `kind: 'all'` yields NO requests. The whole universe is not a scope this
 * apply offers — a ladder is a per-slate regime decision, like forfait. The
 * page cannot currently reach that state (EntryPage renders GroupPickerLanding
 * instead of the table under "All groups", so the bar never mounts there), so
 * this is a fail-closed backstop rather than the primary defence; it is here so
 * that mounting the bar in a wider view cannot silently stamp every rule.
 */
export function buildBulkLadderDtos(
  selection: BulkParamSelection,
  path: 'a' | 'b',
  counterHedgeLadderStepMs: number | null,
  counterHedgeLadderAdverseTicks: number | null,
  scopedRuleIds: string[]
): BulkParamsDto[] {
  const values = {
    counterHedgeLadderStepMs,
    counterHedgeLadderAdverseTicks
  };
  if (selection.kind === 'all') return [];
  if (selection.kind === 'group')
    return [{ scope: 'all', groupId: selection.groupId, path, ...values }];
  if (selection.kind === 'subgroup')
    return [
      { scope: 'all', subgroupId: selection.subgroupId, path, ...values }
    ];
  return chunkIds(scopedRuleIds, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    path,
    ...values
  }));
}

export interface BulkMakerFlagDto {
  scope: 'all' | 'ids';
  ruleIds?: string[];
  groupId?: string;
  subgroupId?: string;
  flag: MakerFlag;
  value: boolean;
}

/**
 * Scope a maker-flag flip can target: the page's group filter (all / a real
 * group / ungrouped), a concrete subgroup nested inside the selected group, OR
 * an explicit set of checked rows. The subgroup branch is server-narrowed
 * exactly like a real group, but via `subgroupId` instead of `groupId` (groupId
 * XOR subgroupId, never both). The `selected` branch carries its own rule ids
 * inline and rides scope:'ids' — it lets the PM/KM ON/OFF controls flip just
 * the checked subset, mirroring the market-data "Enable/Disable selected".
 */
export type BulkMakerSelection =
  | GroupSelection
  | { kind: 'subgroup'; subgroupId: string }
  | { kind: 'selected'; ruleIds: string[] };

/**
 * Bulk maker-flag DTO(s) — flips ONE of the four maker flags across the
 * selected scope. Mirrors `buildBulkParamsDtos`: "All groups" keeps the
 * single scope:'all' request; a real group narrows scope:'all' server-side
 * via `groupId`; a concrete subgroup narrows scope:'all' server-side via
 * `subgroupId` (so the backend flips the whole subgroup without the UI
 * shipping ids); "Ungrouped" rides scope:'ids' over the group-scoped
 * (pre-search) rule ids, chunked at the backend's 500-id cap; "selected"
 * rides scope:'ids' over its own inline checked-row rule ids. An empty id
 * list yields NO requests. `value:true` may ACTIVATE paused rules
 * server-side — the caller's confirm dialog warns about that.
 */
export function buildBulkMakerFlagDtos(
  selection: BulkMakerSelection,
  flag: MakerFlag,
  value: boolean,
  scopedRuleIds: string[]
): BulkMakerFlagDto[] {
  if (selection.kind === 'all') return [{ scope: 'all', flag, value }];
  if (selection.kind === 'group') {
    return [{ scope: 'all', groupId: selection.groupId, flag, value }];
  }
  if (selection.kind === 'subgroup') {
    return [{ scope: 'all', subgroupId: selection.subgroupId, flag, value }];
  }
  // `selected` carries its own rule ids; ungrouped uses the group-scoped ids.
  const ids = selection.kind === 'selected' ? selection.ruleIds : scopedRuleIds;
  return chunkIds(ids, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    flag,
    value
  }));
}

/**
 * What one bulk WRITE answers. The counters are the whole contract: a refused
 * rule is `skipped`, never an error, so the request is a 200 and react-query's
 * `onError` never fires.
 */
export interface BulkWriteResult {
  matched?: number;
  modified: number;
  skipped?: number;
  skippedReasons?: string[];
  /**
   * Rules the server could not observe the outcome of — a fan-out to a group's
   * own pod that timed out while that pod very likely kept applying them.
   * Neither applied nor refused, and saying either is a lie the operator acts
   * on.
   */
  indeterminate?: number;
}

/**
 * The cause of a skip, with the per-rule specifics blanked out. Mirrors the
 * backend's own `skipReasonCause`.
 *
 * Blanked: what identifies the ROW — a pair name, a ticker, a market id, and
 * Polymarket's boolean tuple (eight spellings of one verdict). KEPT: the
 * status word, the HTTP code, the group name — each of those is a different
 * job for the operator, and merging them drops the one they had not seen yet.
 */
function skipCause(message: string): string {
  return message
    .replace(/"[^"]*"/g, '"…"')
    .replace(/\b[0-9a-f]{24}\b/g, '<id>')
    // A ticker looks exactly like a group name in caps (`MLB-0824`), and a
    // group name is a CAUSE — a different group to go start. Single-quoted
    // spans hold group names, so they pass through untouched.
    .replace(/'[^']*'|\b[A-Z][A-Z0-9]*(?:[-_][A-Z0-9]+)+\b/g, match =>
      match.startsWith("'") ? match : '<market>'
    )
    .replace(/=(?:true|false)\b/g, '=…')
    .replace(/\([^)]*\)|\b\d+\b/g, match =>
      match.startsWith('(') ? match : '#'
    )
    .trim();
}

/** At most this many distinct causes make it into the toast. */
const REPORTED_CAUSES = 3;

const asCount = (n: unknown): number =>
  typeof n === 'number' && Number.isFinite(n) ? n : 0;

/**
 * Raise the warning without ever being able to break the caller.
 *
 * This is advisory: it tells the operator what a bulk did. It runs inside the
 * write path, so a toast that throws — a library shim, a mock, a detached
 * container — would abort a bulk that the server IS applying, which is worse
 * than any missing message.
 */
function warnQuietly(message: string, autoClose: number): void {
  try {
    toast.warning(message, { autoClose });
  } catch {
    // eslint-disable-next-line no-console
    console.warn(message);
  }
}

interface BulkTotals {
  action: string;
  matched: number;
  modified: number;
  skipped: number;
  indeterminate: number;
  passes: number;
  byCause: Map<string, string>;
  interrupted: boolean;
}

const emptyTotals = (action: string): BulkTotals => ({
  action,
  matched: 0,
  modified: 0,
  skipped: 0,
  indeterminate: 0,
  passes: 0,
  byCause: new Map(),
  interrupted: false
});

/**
 * The thing a gesture collects into. Handed to the gesture body and passed
 * BACK IN to every bulk call it makes.
 *
 * Explicit, and never inferred from module state: a flag saying "a gesture is
 * open" cannot tell a call made INSIDE that gesture from one an operator made
 * by clicking another button while it is still in flight. Reading it that way
 * folded a refused DISARM into a successful turn-on's report and left the
 * disarm unreported — the operator walks away from rules still quoting. A
 * parameter also means nothing has to be serialized to keep attribution
 * straight, and a queue a nested call can wait on is a deadlock.
 */
export type BulkCollector = BulkTotals;

/**
 * Tell the operator when a bulk write did LESS than they asked for.
 *
 * The opener refuses a rule per-rule (a closed market, a stopped group) and
 * folds every refusal into a `skipped` counter behind a 200, so nothing else
 * in the app surfaces it: a slate the closed-market gate turned down looks
 * exactly like a slate that armed.
 */
export function reportBulkOutcome(
  action: string,
  totals: {
    matched: number;
    modified: number;
    skipped: number;
    indeterminate: number;
    reasons: string[];
    interrupted?: boolean;
    /** How many bulk CALLS this report covers. >1 means it counts writes. */
    passes: number;
  }
): void {
  // Nothing was even attempted — a gesture that died in a step before its
  // first bulk call. There is no outcome to report, and `MutationCache.onError`
  // is already raising the failure itself.
  if (totals.passes === 0) return;

  const shown = totals.reasons.slice(0, REPORTED_CAUSES);
  const hidden = totals.reasons.length - shown.length;
  const why = shown.length
    ? ` — ${shown.join(' · ')}${hidden > 0 ? ` (+${hidden} more)` : ''}`
    : '';

  // The request never came back. Everything counted is a PREFIX and the server
  // may still be applying the rest — "nothing was touched" is the one thing we
  // know is not true.
  //
  if (totals.interrupted) {
    const unknown =
      totals.indeterminate > 0 ? `, ${totals.indeterminate} unknown` : '';
    warnQuietly(
      `Bulk ${action}: interrupted — ${totals.modified} applied and ` +
        `${totals.skipped} skipped${unknown} so far, the server may STILL be ` +
        `applying the rest; refresh before retrying${why}`,
      15000
    );
    return;
  }

  if (totals.indeterminate > 0) {
    warnQuietly(
      `Bulk ${action}: ${totals.modified} applied, ${totals.skipped} skipped, ` +
        `${totals.indeterminate} unknown — those may STILL be being applied; ` +
        `refresh before retrying${why}`,
      15000
    );
    return;
  }

  if (totals.skipped <= 0 && totals.matched > 0) return;
  if (totals.matched === 0) {
    warnQuietly(`Bulk ${action}: nothing matched — no rule was touched.`, 8000);
    return;
  }
  // A gesture makes several passes over the SAME rules (the soccer turn-on
  // clears two flags, sets two, then arms), so its counters add up WRITES, not
  // rules — and calling them rules would report more than exist.
  const unit = totals.passes > 1 ? 'write' : 'rule';
  const headline =
    totals.modified === 0
      ? `NOTHING changed — all ${totals.skipped} ${
          totals.skipped === 1 ? `${unit} was` : `${unit}s were`
        } skipped`
      : `${totals.modified} ${unit}${totals.modified === 1 ? '' : 's'} ` +
        `applied, ${totals.skipped} skipped`;
  warnQuietly(`Bulk ${action}: ${headline}${why}`, 12000);
}

/**
 * One operator GESTURE, however many bulk calls it takes.
 *
 * The soccer turn-on fires ten behind one button; reporting each stacks ten
 * warnings for what the operator experienced as a single action. Every call
 * inside must be handed the collector this passes to `run`, or it reports
 * separately.
 */
export async function asBulkGesture<T>(
  action: string,
  run: (collect: BulkCollector) => Promise<T>
): Promise<T> {
  const gesture = emptyTotals(action);
  try {
    return await run(gesture);
  } catch (err) {
    gesture.interrupted = true;
    throw err;
  } finally {
    reportBulkOutcome(gesture.action, {
      matched: gesture.matched,
      modified: gesture.modified,
      skipped: gesture.skipped,
      indeterminate: gesture.indeterminate,
      passes: gesture.passes,
      reasons: [...gesture.byCause.values()],
      interrupted: gesture.interrupted
    });
  }
}

/**
 * Runs every chunk of one bulk call, into `collect` when it belongs to a
 * gesture or into its own report when it stands alone.
 */
async function applyAndReport<T>(
  action: string,
  dtos: T[],
  apply: (dto: T) => Promise<BulkWriteResult>,
  collect?: BulkCollector
): Promise<number> {
  if (!collect) {
    return asBulkGesture(action, own =>
      applyAndReport(action, dtos, apply, own)
    );
  }
  const into = collect;
  let modified = 0;
  try {
    into.passes += 1;
    for (const dto of dtos) {
      const res = await apply(dto);
      into.matched += asCount(res?.matched);
      into.modified += asCount(res?.modified);
      into.skipped += asCount(res?.skipped);
      into.indeterminate += asCount(res?.indeterminate);
      modified += asCount(res?.modified);
      for (const reason of Array.isArray(res?.skippedReasons)
        ? res.skippedReasons
        : []) {
        const cause = skipCause(reason);
        if (!into.byCause.has(cause)) into.byCause.set(cause, reason);
      }
    }
  } catch (err) {
    into.interrupted = true;
    throw err;
  }
  return modified;
}

/**
 * Applies each bulk-maker-flag DTO sequentially and sums the per-chunk
 * `modified` counters. Sequential on purpose — mirrors `applyBulkParamsDtos`
 * so a mid-loop failure leaves a clean prefix applied.
 */
export async function applyBulkMakerFlagDtos(
  dtos: BulkMakerFlagDto[],
  apply: (dto: BulkMakerFlagDto) => Promise<BulkWriteResult>,
  collect?: BulkCollector
): Promise<number> {
  const on = dtos[0]?.value === true;
  return applyAndReport(`maker ${on ? 'ON' : 'OFF'}`, dtos, apply, collect);
}

export interface BulkArmedDto {
  scope: 'all' | 'ids';
  ruleIds?: string[];
  groupId?: string;
  subgroupId?: string;
  armed: boolean;
}

/**
 * Scope a bulk arm/disarm can target — mirrors `BulkMakerSelection`: the page's
 * group filter (all / a real group / ungrouped), a concrete subgroup nested
 * inside the selected group, OR an explicit set of checked rows. `desiredArmed`
 * is now the ONLY arm/disarm trigger (a maker-flag flip no longer arms), so this
 * is the bulk equivalent of the per-row on/off switch's explicit boolean.
 */
export type BulkArmedSelection =
  | GroupSelection
  | { kind: 'subgroup'; subgroupId: string }
  | { kind: 'selected'; ruleIds: string[] };

/**
 * Bulk arm/disarm DTO(s) — sets `desiredArmed` across the selected scope in one
 * round trip. Byte-identical in structure to `buildBulkMakerFlagDtos` (with
 * `armed` replacing `flag`/`value`): "All groups" keeps the single scope:'all'
 * request; a real group narrows scope:'all' server-side via `groupId`; a
 * concrete subgroup narrows scope:'all' server-side via `subgroupId`;
 * "Ungrouped" rides scope:'ids' over the group-scoped (pre-search) rule ids,
 * chunked at the backend's 500-id cap; "selected" rides scope:'ids' over its own
 * inline checked-row rule ids. An empty id list yields NO requests. The backend
 * itself fans grouped arming to the right feed pods, so the FE always calls the
 * one `/rules/bulk-armed` endpoint on the core base.
 */
export function buildBulkArmedDtos(
  selection: BulkArmedSelection,
  armed: boolean,
  scopedRuleIds: string[]
): BulkArmedDto[] {
  if (selection.kind === 'all') return [{ scope: 'all', armed }];
  if (selection.kind === 'group') {
    return [{ scope: 'all', groupId: selection.groupId, armed }];
  }
  if (selection.kind === 'subgroup') {
    return [{ scope: 'all', subgroupId: selection.subgroupId, armed }];
  }
  // `selected` carries its own rule ids; ungrouped uses the group-scoped ids.
  const ids = selection.kind === 'selected' ? selection.ruleIds : scopedRuleIds;
  return chunkIds(ids, BULK_PARAMS_RULE_IDS_CHUNK).map(ruleIds => ({
    scope: 'ids',
    ruleIds,
    armed
  }));
}

/**
 * Applies each bulk-armed DTO sequentially and sums the per-chunk `modified`
 * counters. Sequential on purpose — mirrors `applyBulkMakerFlagDtos` so a
 * mid-loop failure leaves a clean prefix applied.
 */
export async function applyBulkArmedDtos(
  dtos: BulkArmedDto[],
  apply: (dto: BulkArmedDto) => Promise<BulkWriteResult>,
  collect?: BulkCollector
): Promise<number> {
  // The action has to come from the DTO: the group control is DISARM-only, and
  // a toast that says "arm" over a failed disarm sends the operator away from
  // rules that are still armed and quoting.
  const arming = dtos[0]?.armed === true;
  return applyAndReport(arming ? 'arm' : 'disarm', dtos, apply, collect);
}
